import React, { useMemo, useState } from "react";
import { StyleSheet } from "react-native";
import * as Animatable from "react-native-animatable";
import i18n from "@microdoing/shared/i18n";
import theme from "app/Theme/Theme";
import { pluralize } from "app/utils/plurialize";
import { Box, MainText, PressableOpacity } from "app/components/common";
import Loader from "app/components/common/Loader";
import normalize from "app/utils/normalize";
import MultipleAvatarSquare from "app/components/common/MultipleAvatarSquare";
import { IUserInfoTeam } from "@microdoing/shared/services/team/types";
import LightningIcon from "app/components/Icons/LightningIcon";
import { useSelector } from "react-redux";
import { getTime } from "@microdoing/shared/services/general/selectors";
import CheckIcon from "app/components/Icons/CheckIcon";
import moment from "moment";

const PULSE_WIDTH = normalize(80);

const styles = StyleSheet.create({
  pulseButton: {
    width: PULSE_WIDTH,
    backgroundColor: theme.colors["brand.100"],
    borderRadius: 8,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
});

interface IProps {
  user: IUserInfoTeam;
  onPress: () => void;
  onPressPulse: () => void;
  isUser?: boolean;
  hasPulse?: boolean;
}

const TeamRow = ({
  user,
  onPress,
  onPressPulse,
  isUser = false,
  hasPulse = false,
}: IProps) => {
  const [isPulseAnimated, setIsPulseAnimated] = useState(false);
  const [pulseSentDate, setPulseSentDate] = useState<Date | null>(null);

  const time = useSelector(getTime);

  const { name = "", weekActions = 0 } = user;

  const handlePulse = () => {
    setIsPulseAnimated(true);
    onPressPulse();
    setTimeout(() => {
      setIsPulseAnimated(false);
      setPulseSentDate(new Date());
    }, 1150);
  };

  const hasAlreadySentPulseToday = useMemo(() => {
    if (!!pulseSentDate && !!time) {
      return moment(pulseSentDate).isSame(moment(time), "day");
    }
    return false;
  }, [time, pulseSentDate]);

  const renderPulseButton = () => {
    if (isUser)
      return (
        <Box alignItems="center" justifyContent="center" flexDirection="row">
          <MainText color="brand.500" variant="l4-medium" marginRight="s">
            {i18n.t("my-profile")}
          </MainText>
        </Box>
      );
    if (hasAlreadySentPulseToday)
      return (
        <Box
          alignItems="center"
          width={PULSE_WIDTH}
          justifyContent="center"
          flexDirection="row"
        >
          <CheckIcon color={theme.colors["system.green.900"]} />
        </Box>
      );
    else {
      return (
        <PressableOpacity
          disabled={isPulseAnimated}
          style={[styles.pulseButton]}
          onPress={handlePulse}
        >
          {isPulseAnimated ? (
            <Loader size={20} />
          ) : (
            <MainText variant="l3-semibold" color="brand.500">
              {i18n.t("pulse")}
            </MainText>
          )}
        </PressableOpacity>
      );
    }
  };

  return (
    <Box marginTop={isUser ? "sm" : "sm"}>
      <PressableOpacity
        onPress={onPress}
        hitSlop={{ top: 13, bottom: 13, left: 13, right: 13 }}
      >
        <Box alignItems="center" flexDirection="row">
          <Animatable.View
            useNativeDriver
            animation={hasPulse ? "bounce" : null}
            iterationCount={4}
            style={[
              hasPulse && {
                transform: [{ scaleX: 2.1 }, { scaleY: 2.1 }],
              },
              {
                justifyContent: "center",
              },
            ]}
          >
            {weekActions > 0 && (
              <Box
                position="absolute"
                zIndex={10}
                width={normalize(16)}
                height={normalize(16)}
                padding={2}
                right={0}
                bottom={0}
                alignItems="center"
                justifyContent="center"
                borderRadius={normalize(8)}
                borderWidth={normalize(1)}
                borderColor="neutral.white"
                backgroundColor="brand.500"
              >
                <LightningIcon height={normalize(12)} width={normalize(12)} />
              </Box>
            )}
            <MultipleAvatarSquare size={normalize(50)} users={[user]} />
          </Animatable.View>
          <Box flex={1} marginLeft="s" marginRight="s">
            <MainText variant="l5-medium" numberOfLines={1}>
              {name}
            </MainText>
            {weekActions > 0 && (
              <MainText marginTop={2} color="neutral.600" variant="l3-medium">
                +{weekActions} {pluralize("point", weekActions)}
              </MainText>
            )}
          </Box>

          {renderPulseButton()}
        </Box>
      </PressableOpacity>
    </Box>
  );
};

export default TeamRow;
